import React from "react";
import {
	App as AntdApp,
	Alert,
	Button,
	Collapse,
	Descriptions,
	Empty,
	Modal,
	Space,
	Spin,
	Table,
	Tag,
	Typography
} from "antd";
import {ExclamationCircleOutlined, UnlockOutlined, ReloadOutlined} from "@ant-design/icons";

import api from "@/api/axios.js";

const {Paragraph, Text} = Typography;

function buildCounts(summary)
{
	const counts = summary?.counts || {};
	return [
		{label: "Card in errore", value: counts.errorCards ?? 0, color: counts.errorCards > 0 ? "red" : "default"},
		{label: "Zone in errore", value: counts.errorZones ?? 0, color: counts.errorZones > 0 ? "orange" : "default"},
		{label: "Card completate", value: counts.completedCount ?? 0, color: counts.completedCount > 0 ? "green" : "default"},
		{label: "Card totali", value: counts.totalCards ?? 0, color: "blue"}
	];
}

export default function FlyerErrorModal({open, onClose, flyerId, flyerName, onUpdated})
{
	const {message} = AntdApp.useApp();
	const [loading, setLoading] = React.useState(false);
	const [report, setReport] = React.useState(null);
	const [unlockingAll, setUnlockingAll] = React.useState(false);
	const [unlockingGlobal, setUnlockingGlobal] = React.useState(false);
	const [unlockingSerials, setUnlockingSerials] = React.useState([]);

	const loadReport = React.useCallback(async () =>
	{
		if (!flyerId)
		{
			setReport(null);
			return;
		}

		setLoading(true);
		try
		{
			const {data} = await api.get(`/api/flyers/${flyerId}/error-report`);
			setReport(data || null);
		}
		catch (error)
		{
			setReport(null);
			message.error(error?.response?.data?.error || "Impossibile caricare il report errori");
		}
		finally
		{
			setLoading(false);
		}
	}, [flyerId, message]);

	React.useEffect(() =>
	{
		if (open)
			void loadReport();
	}, [open, loadReport]);

	const handleUnlock = React.useCallback(async (serials = []) =>
	{
		const normalized = Array.isArray(serials) ? serials.filter((value) => Number.isInteger(value) && value >= 1) : [];
		const isBulk = normalized.length === 0;

		if (isBulk)
			setUnlockingAll(true);
		else
			setUnlockingSerials((prev) => [...prev, ...normalized.filter((value) => !prev.includes(value))]);

		try
		{
			const {data} = await api.post(`/api/flyers/${flyerId}/error-report/unlock`, {
				serials: normalized
			});
			const unlockedCount = Array.isArray(data?.unlockedSerials) ? data.unlockedSerials.length : 0;
			message.success(
				isBulk
					? `Sbloccate ${unlockedCount} posizioni in errore`
					: `Sbloccata la posizione ${normalized[0]}`
			);
			await loadReport();
			onUpdated?.(data?.flyer || null);
		}
		catch (error)
		{
			message.error(error?.response?.data?.error || "Impossibile sbloccare le posizioni selezionate");
		}
		finally
		{
			if (isBulk)
				setUnlockingAll(false);
			else
				setUnlockingSerials((prev) => prev.filter((value) => !normalized.includes(value)));
		}
	}, [flyerId, loadReport, message, onUpdated]);

	const handleUnlockGlobalError = React.useCallback(async () =>
	{
		if (!flyerId)
			return;

		setUnlockingGlobal(true);
		try
		{
			const {data} = await api.post(`/api/flyers/${flyerId}/processing/unlock`);
			message.success("Errore globale sbloccato");
			await loadReport();
			onUpdated?.(data?.flyer || null);
		}
		catch (error)
		{
			message.error(error?.response?.data?.error || "Impossibile sbloccare l'errore globale");
		}
		finally
		{
			setUnlockingGlobal(false);
		}
	}, [flyerId, loadReport, message, onUpdated]);

	const items = Array.isArray(report?.items) ? report.items : [];
	const summary = report?.summary || null;
	const counts = buildCounts(summary);
	const technicalMessages = Array.isArray(summary?.technicalMessages) ? summary.technicalMessages : [];
	const status = report?.flyer?.status || null;
	const hasGlobalOnlyError = status === "ERROR" && items.length === 0 && Boolean(summary?.lastProcessingError);

	const columns = React.useMemo(() => [
		{
			title: "Seriale",
			dataIndex: "seriale",
			width: 100,
			render: (value) => value ?? "—"
		},
		{
			title: "Pagina",
			dataIndex: "page",
			width: 90,
			render: (value) => value ?? "—"
		},
		{
			title: "Crop",
			dataIndex: "crop",
			width: 90,
			render: (value) => value ?? "—"
		},
		{
			title: "Tipo",
			dataIndex: "type",
			width: 110,
			render: (value) => <Tag color={value === "banner" ? "gold" : "geekblue"}>{value || "gabbia"}</Tag>
		},
		{
			title: "Motivo tecnico",
			dataIndex: "technicalReason",
			render: (value) => value ? <Text code>{value}</Text> : <Text type="secondary">Non disponibile</Text>
		},
		{
			title: "Azione",
			key: "actions",
			width: 140,
			render: (_, record) =>
			{
				const seriale = Number(record?.seriale);
				const isLoading = unlockingSerials.includes(seriale);
				const disabled = !Number.isInteger(seriale) || seriale < 1;
				return (
					<Button
						size="small"
						icon={<UnlockOutlined />}
						loading={isLoading}
						disabled={disabled}
						onClick={() => handleUnlock([seriale])}
					>
						Sblocca
					</Button>
				);
			}
		}
	], [handleUnlock, unlockingSerials]);

	return (
		<Modal
			open={open}
			onCancel={onClose}
			title={
				<Space>
					<ExclamationCircleOutlined style={{color: "#d4380d"}} />
					<span>Gestione errori volantino</span>
				</Space>
			}
			width={1120}
			footer={[
				<Button key="refresh" icon={<ReloadOutlined />} onClick={() => void loadReport()} disabled={loading || unlockingAll}>
					Aggiorna
				</Button>,
				<Button key="close" onClick={onClose}>
					Chiudi
				</Button>,
				hasGlobalOnlyError && (
					<Button
						key="unlock-global"
						type="primary"
						icon={<UnlockOutlined />}
						loading={unlockingGlobal}
						disabled={loading || unlockingAll}
						onClick={() => void handleUnlockGlobalError()}
					>
						Sblocca errore globale
					</Button>
				),
				<Button
					key="unlock-all"
					type="primary"
					danger
					icon={<UnlockOutlined />}
					loading={unlockingAll}
					disabled={!items.length}
					onClick={() => void handleUnlock([])}
				>
					Sblocca tutte le posizioni in errore
				</Button>
			]}
		>
			<Spin spinning={loading}>
				<Space direction="vertical" size={16} style={{width: "100%"}}>
					<Descriptions
						size="small"
						column={2}
						items={[
							{key: "flyer", label: "Volantino", children: flyerName || report?.flyer?.name || "—"},
							{key: "status", label: "Stato", children: <Tag color={status === "ERROR" ? "red" : "blue"}>{status || "—"}</Tag>}
						]}
					/>

					{summary && (
						<Alert
							type="error"
							showIcon
							message={summary.title || "Errore di elaborazione"}
							description={
								<Space direction="vertical" size={12} style={{width: "100%"}}>
									<div>{summary.message}</div>
									<Space size={[8, 8]} wrap>
										{counts.map((item) => (
											<Tag key={item.label} color={item.color}>
												{item.label}: {item.value}
											</Tag>
										))}
									</Space>
								</Space>
							}
						/>
					)}

					<Collapse
						items={[
							{
								key: "technical-log",
								label: "Log tecnico",
								children: technicalMessages.length ? (
									<Space direction="vertical" size={8} style={{width: "100%"}}>
										{technicalMessages.map((entry, index) => (
											<Paragraph
												key={`${entry}-${index}`}
												copyable
												style={{
													marginBottom: 0,
													background: "#fafafa",
													border: "1px solid #f0f0f0",
													borderRadius: 8,
													padding: 12,
													fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace"
												}}
											>
												{entry}
											</Paragraph>
										))}
									</Space>
								) : (
									<Text type="secondary">Nessun log tecnico disponibile.</Text>
								)
							}
						]}
					/>

					<div>
						<Text strong>Posizioni bloccate</Text>
						<div style={{marginTop: 8}}>
							{items.length ? (
								<Table
									size="small"
									rowKey={(record) => record.key}
									columns={columns}
									dataSource={items}
									pagination={false}
								/>
							) : (
								<Empty
									image={Empty.PRESENTED_IMAGE_SIMPLE}
									description="Nessuna posizione in errore da mostrare"
								/>
							)}
						</div>
					</div>
				</Space>
			</Spin>
		</Modal>
	);
}
